import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import api, { API, errMsg, TOKEN_KEY } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/context/LanguageContext';
import { Stepper } from '@/components/studio/Stepper';
import { Step1Reference } from '@/components/studio/Step1Reference';
import { Step2Photos } from '@/components/studio/Step2Photos';
import { Step3Prompt } from '@/components/studio/Step3Prompt';
import { Step4Summary } from '@/components/studio/Step4Summary';
import { PreviewPanel } from '@/components/studio/PreviewPanel';
import { Button } from '@/components/ui/button';

export default function Studio() {
  const { user, setCredits, refresh } = useAuth();
  const { t } = useI18n();

  const [step, setStep] = useState(1);
  const [coverType, setCoverType] = useState('youtube');
  const orientation = coverType === 'shorts' ? 'portrait' : 'landscape';
  const [reference, setReference] = useState(null);
  const [usePhoto, setUsePhoto] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [selectedPhotoId, setSelectedPhotoId] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [texts, setTexts] = useState('');
  const [extras, setExtras] = useState([]);
  const [fidelity, setFidelity] = useState('exact');
  const [generation, setGeneration] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pollRef = useRef(null);

  const STEPS = [
    { title: t('studio.step1Title'), hint: t('studio.step1Hint') },
    { title: t('studio.step2Title'), hint: t('studio.step2Hint') },
    { title: t('studio.step3Title'), hint: t('studio.step3Hint') },
    { title: t('studio.step4Title'), hint: t('studio.step4Hint') },
  ];

  const FIDELITY_LABELS = {
    exact: t('studio.fidelityExact'),
    creative: t('studio.fidelityCreative'),
  };

  const reloadPhotos = useCallback(async () => {
    try {
      const { data } = await api.get('/photos');
      setPhotos(data.photos);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    reloadPhotos();
  }, [reloadPhotos]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/generations/${id}`);
        setGeneration(data);
        if (data.status === 'done' || data.status === 'failed') {
          clearInterval(pollRef.current);
          setGenerating(false);
          await refresh();
          if (data.status === 'done') toast.success(t('studio.coverReady'));
          else
            toast.error(
              t('studio.genFailed', { error: data.error || t('studio.unknownError') })
            );
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setGenerating(false);
        toast.error(errMsg(e, t('studio.statusFetchFailed')));
      }
    }, 2500);
  };

  const generate = async () => {
    if (!reference && !prompt.trim() && !texts.trim()) {
      toast.error(t('studio.needSomething'));
      return;
    }
    setGenerating(true);
    setGeneration(null);
    try {
      const { data } = await api.post('/generate', {
        orientation,
        cover_type: coverType,
        prompt,
        texts,
        fidelity,
        reference_file_id: reference?.file_id || null,
        person_photo_id: usePhoto ? selectedPhotoId : null,
        extra_file_ids: extras.map((x) => x.file_id),
        title: reference?.title || null,
        source: reference?.source || 'manual',
      });
      setGeneration(data.generation);
      setCredits(data.credits);
      poll(data.generation.id);
    } catch (e) {
      setGenerating(false);
      toast.error(errMsg(e, t('studio.startFailed')));
    }
  };

  const download = async () => {
    if (!generation?.id) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API}/generations/${generation.id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `spunkram-${orientation}-${generation.id.slice(0, 8)}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(t('studio.downloadStarted'));
    } catch (e) {
      toast.error(t('studio.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const resetAll = () => {
    setGeneration(null);
    setReference(null);
    setPrompt('');
    setTexts('');
    setExtras([]);
    setSelectedPhotoId(null);
    setUsePhoto(false);
    setStep(1);
  };

  const canGoTo = (n) => {
    if (n === 1) return true;
    return !!reference || !!prompt.trim() || !!texts.trim() || n <= step;
  };

  const summary = [
    {
      label: t('studio.sum.coverType'),
      value: coverType === 'shorts' ? t('studio.sum.ytShorts') : t('studio.sum.youtube'),
    },
    { label: t('studio.sum.output'), value: orientation === 'portrait' ? '1080 x 1920' : '1920 x 1080' },
    {
      label: t('studio.sum.reference'),
      value: reference
        ? reference.source === 'youtube'
          ? t('studio.sum.refYoutube')
          : t('studio.sum.refUpload')
        : t('common.none'),
    },
    {
      label: t('studio.sum.photo'),
      value: usePhoto && selectedPhotoId ? t('studio.sum.photoSelected') : t('studio.sum.photoUnused'),
    },
    { label: t('studio.sum.extras'), value: t('common.pieces', { count: extras.length }) },
    {
      label: t('studio.sum.texts'),
      value: texts.trim() ? texts.trim().slice(0, 28) : t('studio.sum.textsUnchanged'),
    },
    { label: t('studio.sum.fidelity'), value: FIDELITY_LABELS[fidelity] },
    { label: t('studio.sum.cost'), value: t('studio.sum.costValue') },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{t('studio.title')}</h1>
          <p className="mt-1 text-sm text-[hsl(var(--text-muted))]">{t('studio.subtitle')}</p>
        </div>

        <div className="rounded-[12px] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] px-4 py-2.5">
          <span className="text-[11px] text-[hsl(var(--text-muted))]">{t('studio.selectedFormat')}</span>
          <p data-testid="selected-format-label" className="font-mono-alt text-sm font-semibold text-[hsl(var(--brand-red))]">
            {coverType === 'shorts' ? 'YT Shorts - 1080x1920' : 'YouTube - 1920x1080'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7 xl:col-span-8">
          <div className="panel p-4 sm:p-6">
            <Stepper steps={STEPS} current={step} onSelect={setStep} canGoTo={canGoTo} />
            <div className="my-5 h-px bg-[hsl(var(--border-soft))]" />

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
              >
                {step === 1 && (
                  <Step1Reference
                    reference={reference}
                    setReference={setReference}
                    coverType={coverType}
                    setCoverType={setCoverType}
                  />
                )}
                {step === 2 && (
                  <Step2Photos
                    usePhoto={usePhoto}
                    setUsePhoto={setUsePhoto}
                    photos={photos}
                    selectedPhotoId={selectedPhotoId}
                    setSelectedPhotoId={setSelectedPhotoId}
                    reloadPhotos={reloadPhotos}
                  />
                )}
                {step === 3 && (
                  <Step3Prompt
                    prompt={prompt}
                    setPrompt={setPrompt}
                    texts={texts}
                    setTexts={setTexts}
                    extras={extras}
                    setExtras={setExtras}
                    fidelity={fidelity}
                    setFidelity={setFidelity}
                  />
                )}
                {step === 4 && (
                  <Step4Summary
                    credits={user?.credits ?? 0}
                    onGenerate={generate}
                    generating={generating}
                    orientation={orientation}
                    summary={summary}
                  />
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-7 flex items-center justify-between gap-3 border-t border-[hsl(var(--border-soft))] pt-5">
              <Button
                variant="ghost"
                disabled={step === 1}
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                data-testid="wizard-back-button"
                className="text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--surface-2))] hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> {t('common.back')}
              </Button>
              {step < 4 ? (
                <Button
                  onClick={() => setStep((s) => Math.min(4, s + 1))}
                  data-testid="wizard-next-button"
                  className="bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))] active:scale-[0.98]"
                >
                  {t('common.next')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <span data-testid="studio-balance-text" className="font-mono-alt text-xs text-[hsl(var(--text-muted))]">
                  {t('studio.balance', { count: user?.credits ?? 0 })}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 xl:col-span-4">
          <div className="lg:sticky lg:top-20">
            <PreviewPanel
              orientation={orientation}
              reference={reference}
              generation={generation}
              generating={generating}
              downloading={downloading}
              onDownload={download}
              onVariation={generate}
              onReset={resetAll}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
